/**
 * HistoryDrawer — slide-in side panel listing up to 20 retained snapshots
 * for a single PAGE object. Each row shows a version number, a relative
 * timestamp ("2 min ago", "Yesterday"), and a Restore button that confirms
 * via `window.confirm` and POSTs to `/pages/:id/revert`.
 *
 * The drawer fetches snapshots lazily (only when `open` is true) via
 * react-query. It calls `onRestored` after a successful revert so the
 * parent can invalidate its own queries — the iframe-patch path from
 * commit 7 takes care of the in-place update when the `page` object
 * re-fetches.
 *
 * Kept isolated in its own file so it can be unit-tested without mounting
 * the entire PagesContainerModule tree.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useQuery } from "@tanstack/react-query";
import { env } from "@/lib/env";
import { formatRelativeTime } from "@/lib/timeUtils";
import { SnapshotPreview } from "./components/SnapshotPreview";
import { authHeaders, fetchSnapshots } from "./lib/snapshots-api";

export type { SnapshotListItem } from "./types";
import type { SnapshotListItem } from "./types";

interface HistoryDrawerProps {
  /** Whether the drawer is visible. */
  open: boolean;
  /** Called when the user dismisses the drawer. */
  onClose: () => void;
  /** Page object id — the object whose snapshots are listed + reverted. */
  pageObjectId: string;
  /** Auth bearer (forwarded as Authorization header). */
  token: string | null;
  /**
   * Fired after a successful revert POST. Parent uses this to invalidate
   * module/object queries so the restored html picks up.
   */
  onRestored?: () => void;
  /**
   * Confirmation function. Defaults to `window.confirm`. Tests can inject
   * a stub to bypass the native dialog.
   */
  confirm?: (message: string) => boolean;
  /**
   * Fetch override. Defaults to `globalThis.fetch`. Tests stub this to
   * avoid real network calls.
   */
  fetchFn?: typeof fetch;
}

const DRAWER_WIDTH = 360;

export default function HistoryDrawer({
  open,
  onClose,
  pageObjectId,
  token,
  onRestored,
  confirm,
  fetchFn = globalThis.fetch,
}: HistoryDrawerProps) {
  const [restoringId, setRestoringId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Inline confirm: which snapshot the user has armed for restore. null
  // means "no confirm prompt showing". Used instead of window.confirm so
  // the drawer's visual language isn't broken by the native browser
  // modal. Tests can still inject a `confirm` stub — when provided, the
  // stub bypasses the inline prompt entirely (legacy behavior).
  const [pendingRestoreId, setPendingRestoreId] = useState<string | null>(null);
  // Transient success banner shown after a successful restore.
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLDivElement>(null);

  const { data: snapshots = [], isLoading } = useQuery({
    queryKey: ["page-snapshots", pageObjectId],
    queryFn: () => fetchSnapshots(fetchFn, pageObjectId, token),
    enabled: open && Boolean(pageObjectId),
    staleTime: 5_000,
  });

  // Focus trap + Escape-to-close. When the drawer opens we focus the
  // close button (standard a11y pattern for side panels) and capture
  // Tab / Shift+Tab to keep focus inside the drawer until it closes.
  useEffect(() => {
    if (!open) return;
    // Defer to next tick so the portal has mounted.
    const raf =
      typeof requestAnimationFrame !== "undefined"
        ? requestAnimationFrame(() => closeBtnRef.current?.focus())
        : (setTimeout(
            () => closeBtnRef.current?.focus(),
            0,
          ) as unknown as number);

    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
        return;
      }
      if (e.key === "Tab") {
        const root = drawerRef.current;
        if (!root) return;
        const focusables = root.querySelectorAll<HTMLElement>(
          'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])',
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const active = document.activeElement as HTMLElement | null;
        if (e.shiftKey && active === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && active === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.removeEventListener("keydown", onKeyDown, true);
      if (typeof cancelAnimationFrame !== "undefined")
        cancelAnimationFrame(raf);
    };
  }, [open, onClose]);

  // Clear transient state when the drawer closes so reopening starts fresh.
  useEffect(() => {
    if (!open) {
      setPendingRestoreId(null);
      setSuccessMsg(null);
      setError(null);
    }
  }, [open]);

  const handleRestore = useCallback(
    async (snapshot: SnapshotListItem) => {
      if (snapshot.versionNumber == null) {
        setError("Snapshot has no version metadata.");
        return;
      }
      // Legacy injection point for tests that stub window.confirm: if a
      // `confirm` override is supplied, use it. Otherwise route through
      // the inline confirm UI.
      if (confirm) {
        const msg = "Restore to this version? Current edits will be lost.";
        if (!confirm(msg)) return;
      } else if (pendingRestoreId !== snapshot.id) {
        setPendingRestoreId(snapshot.id);
        setError(null);
        return;
      }

      setRestoringId(snapshot.id);
      setError(null);
      try {
        const res = await fetchFn(
          `${env.apiBaseUrl}/pages/${encodeURIComponent(pageObjectId)}/revert`,
          {
            method: "POST",
            headers: authHeaders(token),
            body: JSON.stringify({ versionNumber: snapshot.versionNumber }),
          },
        );
        if (!res.ok) throw new Error(`Revert failed: ${res.status}`);
        // Let the parent invalidate its module/object caches.
        onRestored?.();
        setPendingRestoreId(null);
        const versionText =
          snapshot.versionNumber != null
            ? `Restored to Version ${snapshot.versionNumber}.`
            : "Restored the previous version.";
        setSuccessMsg(versionText);
        // Close the drawer after a brief moment so the user reads the
        // success banner. The parent will invalidate caches in the
        // meantime.
        setTimeout(() => {
          onClose();
        }, 900);
      } catch (err) {
        console.error("[HistoryDrawer] Restore failed:", err);
        setError("Restore failed. Please try again.");
        setPendingRestoreId(null);
      } finally {
        setRestoringId(null);
      }
    },
    [
      confirm,
      fetchFn,
      pageObjectId,
      token,
      onRestored,
      onClose,
      pendingRestoreId,
    ],
  );

  const cancelPending = useCallback(() => {
    setPendingRestoreId(null);
    setError(null);
  }, []);

  const content = useMemo(() => {
    if (!open) return null;
    return (
      <>
        {/* Backdrop */}
        <div
          aria-hidden="true"
          onClick={onClose}
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.25)",
            zIndex: 10000,
            animation: "xavior-history-backdrop-fade 0.18s ease-out",
          }}
        />
        {/* Drawer */}
        <aside
          ref={drawerRef}
          role="dialog"
          aria-label="Page history"
          aria-modal="true"
          data-testid="history-drawer"
          style={{
            position: "fixed",
            top: 60,
            right: 0,
            bottom: 0,
            width: DRAWER_WIDTH,
            maxWidth: "100vw",
            background: "var(--color-surface)",
            boxShadow: "-8px 0 24px rgba(15, 23, 42, 0.12)",
            zIndex: 10001,
            display: "flex",
            flexDirection: "column",
            fontFamily: "'DM Sans', sans-serif",
            animation: "xavior-history-drawer-slide-in 0.22s ease-out",
          }}
        >
          <style>{`
            @keyframes xavior-history-drawer-slide-in {
              from { transform: translateX(100%); }
              to   { transform: translateX(0); }
            }
            @keyframes xavior-history-backdrop-fade {
              from { opacity: 0; }
              to   { opacity: 1; }
            }
          `}</style>
          {/* Header */}
          <header
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "12px 16px",
              borderBottom: "1px solid rgba(15, 23, 42, 0.08)",
            }}
          >
            <span
              style={{
                flex: 1,
                fontSize: 14,
                fontWeight: 600,
                color: "var(--color-text)",
              }}
            >
              Version history
            </span>
            <button
              ref={closeBtnRef}
              type="button"
              onClick={onClose}
              aria-label="Close history drawer"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 6,
                color: "var(--color-text-faint)",
                display: "flex",
                alignItems: "center",
                borderRadius: 6,
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.background = "rgba(15, 23, 42, 0.05)")
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.background = "transparent")
              }
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </header>

          {successMsg && (
            <div
              role="status"
              data-testid="history-success-banner"
              style={{
                padding: "8px 16px",
                background: "#dcfce7",
                color: "#166534",
                fontSize: 13,
                fontWeight: 500,
              }}
            >
              {successMsg}
            </div>
          )}

          {error && (
            <div
              role="alert"
              style={{
                padding: "8px 16px",
                background: "#fee2e2",
                color: "#991b1b",
                fontSize: 13,
              }}
            >
              {error}
            </div>
          )}

          {/* Body */}
          <div
            className="xv-scroll"
            style={{
              flex: 1,
              overflowY: "auto",
              padding: "8px 0",
            }}
          >
            {isLoading && (
              <div
                style={{
                  padding: "24px 16px",
                  color: "var(--color-text-faint)",
                  fontSize: 13,
                }}
              >
                Loading history…
              </div>
            )}
            {!isLoading && snapshots.length === 0 && (
              <div
                style={{
                  padding: "24px 16px",
                  color: "var(--color-text-faint)",
                  fontSize: 13,
                }}
              >
                No prior versions yet. Edit this page to start building history.
              </div>
            )}
            {!isLoading &&
              snapshots.map((snapshot) => {
                const label =
                  snapshot.versionNumber != null
                    ? `Version ${snapshot.versionNumber}`
                    : "Snapshot";
                const isPending = pendingRestoreId === snapshot.id;
                return (
                  <article
                    key={snapshot.id}
                    data-testid="history-snapshot-row"
                    style={{
                      padding: "10px 16px",
                      borderBottom: "1px solid rgba(15, 23, 42, 0.04)",
                      display: "flex",
                      flexDirection: "column",
                      gap: 6,
                      background: isPending
                        ? "rgba(99, 102, 241, 0.04)"
                        : "transparent",
                    }}
                  >
                    <div
                      style={{ display: "flex", alignItems: "center", gap: 8 }}
                    >
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: 13,
                            fontWeight: 500,
                            color: "var(--color-text)",
                          }}
                        >
                          {label}
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--color-text-faint)",
                            marginTop: 2,
                          }}
                        >
                          {formatRelativeTime(snapshot.createdAt)}
                        </div>
                      </div>
                      <button
                        type="button"
                        data-testid="history-restore-btn"
                        onClick={() => handleRestore(snapshot)}
                        disabled={restoringId !== null}
                        style={{
                          padding: "6px 12px",
                          border: "1px solid rgba(99, 102, 241, 0.3)",
                          background:
                            restoringId === snapshot.id
                              ? "rgba(99, 102, 241, 0.08)"
                              : "#fff",
                          color: "#4f46e5",
                          fontSize: 12,
                          fontWeight: 500,
                          borderRadius: 6,
                          cursor: restoringId !== null ? "wait" : "pointer",
                          opacity:
                            restoringId !== null && restoringId !== snapshot.id
                              ? 0.5
                              : 1,
                        }}
                      >
                        {restoringId === snapshot.id ? "Restoring…" : "Restore"}
                      </button>
                    </div>
                    {isPending && restoringId === null && (
                      <div
                        data-testid="history-inline-confirm"
                        role="group"
                        aria-label="Confirm restore"
                        style={{
                          marginTop: 4,
                          padding: "10px 12px",
                          background: "var(--color-surface)",
                          border: "1px solid rgba(99, 102, 241, 0.25)",
                          borderRadius: 8,
                          boxShadow: "0 2px 6px rgba(15, 23, 42, 0.05)",
                        }}
                      >
                        <div
                          style={{
                            fontSize: 13,
                            color: "var(--color-text)",
                            marginBottom: 4,
                          }}
                        >
                          Restore this page to <strong>{label}</strong>?
                        </div>
                        <div
                          style={{
                            fontSize: 12,
                            color: "var(--color-text-muted)",
                            marginBottom: 10,
                          }}
                        >
                          Saved {formatRelativeTime(snapshot.createdAt)} ·
                          current edits will be replaced.
                        </div>
                        <div
                          style={{
                            display: "flex",
                            gap: 8,
                            justifyContent: "flex-end",
                          }}
                        >
                          <button
                            type="button"
                            data-testid="history-inline-cancel"
                            onClick={cancelPending}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid rgba(15, 23, 42, 0.15)",
                              background: "var(--color-surface)",
                              color: "var(--color-text-muted)",
                              fontSize: 12,
                              fontWeight: 500,
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            data-testid="history-inline-confirm-btn"
                            onClick={() => handleRestore(snapshot)}
                            style={{
                              padding: "6px 12px",
                              border: "1px solid #4f46e5",
                              background: "#4f46e5",
                              color: "#fff",
                              fontSize: 12,
                              fontWeight: 500,
                              borderRadius: 6,
                              cursor: "pointer",
                            }}
                          >
                            Restore
                          </button>
                        </div>
                      </div>
                    )}
                    <SnapshotPreview
                      pageObjectId={pageObjectId}
                      snapshotId={snapshot.id}
                      token={token}
                      fetchFn={fetchFn}
                      label={label}
                    />
                  </article>
                );
              })}
          </div>
        </aside>
      </>
    );
  }, [
    open,
    onClose,
    snapshots,
    isLoading,
    restoringId,
    handleRestore,
    error,
    pendingRestoreId,
    successMsg,
    cancelPending,
    pageObjectId,
    token,
    fetchFn,
  ]);

  if (!open) return null;
  // Portal into body so the drawer floats above the module layout.
  if (typeof document === "undefined") return null;
  return createPortal(content, document.body);
}
